import React, { useState, useEffect, useCallback, useMemo } from 'react';
import debounce from 'lodash/debounce';
import SearchIcon from '@rsuite/icons/Search';
import api from '../../services/api';
import VisualizarCliente from './Visualizar/index';
import { Container, InputGroup, Pagination, Stack, Table } from 'rsuite';
import { ContainerData } from './styles';
import { useAuth } from '../../hooks/auth';
import { CustomInput } from '../../components/CustomInput';
import { CustomButton } from '../../components/CustomButton';
import { ContentHeader } from '../../components/ContentHeader';
import DuvidasButton from '../../components/DuvidasButton';
import { FaFilter, FaUserCheck } from 'react-icons/fa';
import { useCustomTheme } from '../../hooks/useCustomTheme';
import { CustomCell } from '../../components/CustomCell';
import { CustomHeaderCell } from '../../components/CustomHeaderCell';
import { toastError } from '../../toasts';

const { Column } = Table;

export default function Cliente() {
  const { id_empresa } = useAuth();
  const [clientes, setClientes] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [buscar, setBuscar] = useState('');
  const [dataInicio, setDataInicio] = useState('');
  const [dataFim, setDataFim] = useState('');
  const [qtdPedidos, setQtdPedidos] = useState('');
  const [pag, setPag] = useState(1);
  const [limite, setLimite] = useState(10);
  const [cliente, setCliente] = useState('');
  const [modalCliente, setModalCliente] = useState(false);
  const [openFilters, setOpenFilters] = useState(false);
  const { theme } = useCustomTheme();

  const consultar = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.post('usuario/fidelizados', {
        IdEmpresa: id_empresa,
        DataInicial: dataInicio || undefined,
        DataFinal: dataFim || undefined,
        QtdPedidos: qtdPedidos || undefined,
        Nome: buscar || undefined,
        Limite: limite,
        Pagina: (pag - 1) * limite,
      });

      setClientes(res.data?.data ?? []);
      setTotal(res.data?.count ?? 0);
    } catch (error) {
      toastError(error.message ?? 'Erro ao consultar clientes fidelizados');
    } finally {
      setLoading(false);
    }
  }, [id_empresa, dataInicio, dataFim, qtdPedidos, buscar, limite, pag]);

  const debounceSearch = useMemo(
    () =>
      debounce((valor) => {
        setBuscar(valor);
        setPag(1);
      }, 500),
    [],
  );

  useEffect(() => {
    return () => debounceSearch.cancel();
  }, [debounceSearch]);

  useEffect(() => {
    consultar();
  }, [consultar, limite, pag]);

  const handleFiltrar = () => {
    setPag(1);
  };

  const handleChangeLimit = (dataKey) => {
    setPag(1);
    setLimite(dataKey);
  };

  return (
    <Container
      style={{
        padding: '10px',
        height: 'calc(100vh - 60px)',
        backgroundColor: theme.colors.background,
      }}
    >
      <ContentHeader
        title="Clientes Fidelizados"
        subtitle="Clientes que mais compraram no período selecionado"
        icon={FaUserCheck}
        secondComponent={
          <InputGroup inside style={{ width: '36vw', border: 'none' }}>
            <CustomInput onChange={debounceSearch} />
            <InputGroup.Addon>
              <SearchIcon />
            </InputGroup.Addon>
          </InputGroup>
        }
        thirdComponent={
          <Stack spacing={10}>
            <DuvidasButton tela="clientes-fidelizados" />
            <CustomButton
              appearance="primary"
              onClick={() => {
                setOpenFilters((prev) => !prev);
              }}
            >
              <FaFilter size={20} />
            </CustomButton>
          </Stack>
        }
      />

      <Stack
        style={{
          display: 'flex',
          alignItems: 'flex-end',
          justifyContent: 'flex-end',
          paddingRight: 20,
          height: openFilters ? 100 : 0,
          overflow: 'hidden',
          transition: ' height 0.2s ease-in-out',
          borderBottom: `1px solid ${theme.colors.lightGrayOpacity}`,
          borderBottomWidth: openFilters ? 1 : 0,
          paddingBottom: openFilters ? 10 : 0,
          marginBottom: 14,
        }}
      >
        <ContainerData style={{ display: 'flex', flexDirection: 'column' }}>
          <label htmlFor="dataInicio" style={{ color: theme.colors.text }}>
            Data Inicial{' '}
          </label>
          <CustomInput
            type="date"
            value={dataInicio}
            onChange={setDataInicio}
          />
        </ContainerData>
        <ContainerData style={{ display: 'flex', flexDirection: 'column' }}>
          <label htmlFor="dataInicio" style={{ color: theme.colors.text }}>
            Data Final{' '}
          </label>
          <CustomInput type="date" value={dataFim} onChange={setDataFim} />
        </ContainerData>

        <CustomInput
          type="number"
          placeholder="Qtd. pedidos"
          value={qtdPedidos}
          onChange={setQtdPedidos}
        />
        <CustomButton
          appearance="ghost"
          onClick={handleFiltrar}
          style={{ marginLeft: 10 }}
        >
          <SearchIcon style={{ width: 20, height: 20 }} />
        </CustomButton>
      </Stack>

      <Table
        height={window.innerHeight - 320}
        data={clientes}
        bordered={true}
        loading={loading}
        onRowClick={(rowData) => {
          setCliente(rowData);
          setModalCliente(true);
        }}
        style={{
          overflowY: 'auto',
          borderColor: theme.colors.backgroundSecondary,
          backgroundColor: theme.colors.backgroundSecondary,
          color: theme.colors.textSecondary,
        }}
        locale={{
          emptyMessage: 'Nenhum pedido encontrado',
          loading: 'Carregando...',
        }}
      >
        <Column
          style={{
            background: theme.colors.backgroundSecondary,
            color: theme.colors.textSecondary,
          }}
          flexGrow={1}
          fixed
        >
          <CustomHeaderCell>Nome</CustomHeaderCell>
          <CustomCell dataKey="Nome" />
        </Column>

        <Column
          style={{
            background: theme.colors.backgroundSecondary,
            color: theme.colors.textSecondary,
          }}
          flexGrow={1}
        >
          <CustomHeaderCell>Telefone</CustomHeaderCell>
          <CustomCell dataKey="Telefone" />
        </Column>

        <Column
          style={{
            background: theme.colors.backgroundSecondary,
            color: theme.colors.textSecondary,
          }}
        >
          <CustomHeaderCell>Pedidos</CustomHeaderCell>
          <CustomCell dataKey="Quantidade" />
        </Column>
      </Table>

      <Stack
        justifyContent="flex-end"
        style={{ marginTop: 10, marginBottom: 10 }}
      >
        <Pagination
          prev
          next
          first
          last
          ellipsis
          boundaryLinks
          maxButtons={5}
          size="xs"
          layout={['-', 'limit', '|', 'pager']}
          total={total}
          limitOptions={[10, 30, 50, 100]}
          limit={limite}
          activePage={pag}
          onChangePage={setPag}
          onChangeLimit={handleChangeLimit}
        />
      </Stack>

      {modalCliente && (
        <VisualizarCliente
          modal={modalCliente}
          fecharModal={() => setModalCliente(false)}
          cliente={cliente}
        />
      )}
    </Container>
  );
}
